import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ExternalLink, Pencil, Trash2, X } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Can } from '../../app/guards';
import { Badge, Dialog, Field, PageHeader, Skeleton, Spinner, useConfirm, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { fmtDateTime, fmtEgp, fmtNumber } from '../../lib/format';
import { useSocketEvent } from '../../lib/socket';
import { ExpenseFormDialog } from './ExpenseFormDialog';
import { catName, expenseTone, type ExpenseDetail } from './types';

export function ExpenseDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const [editing, setEditing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const q = useQuery({ queryKey: ['expense', id], queryFn: () => api<ExpenseDetail>(`/expenses/${id}`) });
  useSocketEvent('expense.updated', useCallback((e: { expenseId: string }) => { if (e.expenseId === id) void qc.invalidateQueries({ queryKey: ['expense', id] }); }, [id, qc]));

  const done = () => {
    toast.success(t('common.saved'));
    setRejecting(false);
    void qc.invalidateQueries({ queryKey: ['expense', id] });
    void qc.invalidateQueries({ queryKey: ['expenses'] });
  };
  const review = useMutation({ mutationFn: (a: 'approve' | 'reject') => api(`/expenses/${id}/${a}`, { method: 'POST', json: a === 'reject' ? { reason: reason.trim() } : {} }), onSuccess: done, onError: toast.error });
  const remove = useMutation({ mutationFn: () => api(`/expenses/${id}`, { method: 'DELETE' }), onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['expenses'] }); nav('/expenses'); }, onError: toast.error });

  if (q.isLoading || !q.data) return <Skeleton className="h-96" />;
  const e = q.data;
  const lang = i18n.language;
  const isImage = e.receiptUrl && !/\.pdf(\?|$)/i.test(e.receiptUrl) && !e.receiptUrl.includes('application%2Fpdf');

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={<span className="flex items-center gap-2"><span dir="ltr">{e.number}</span><Badge tone={expenseTone(e.status)} dot>{t(`expenses.statuses.${e.status}`)}</Badge></span>}
        subtitle={`${catName(e.category, lang)} · ${fmtDateTime(e.occurredAt, lang)}`}
        actions={
          <div className="flex flex-wrap gap-2">
            {e.status === 'PENDING' ? (
              <Can perm="expenses:approve">
                <button className="btn-secondary text-danger-fg" onClick={() => setRejecting(true)}><X size={16} /> {t('expenses.reject')}</button>
                <button className="btn-primary" disabled={review.isPending} onClick={() => review.mutate('approve')}>{review.isPending ? <Spinner /> : <Check size={16} />} {t('expenses.approve')}</button>
              </Can>
            ) : null}
            {e.status !== 'APPROVED' ? (
              <>
                <Can perm="expenses:update"><button className="btn-secondary" onClick={() => setEditing(true)}><Pencil size={16} /> {t('common.edit')}</button></Can>
                <Can perm="expenses:delete"><button className="btn-ghost text-danger-fg" onClick={() => confirmDialog(t('expenses.deleteConfirm'), { danger: true }).then((ok) => ok && remove.mutate())}><Trash2 size={16} /> {t('common.delete')}</button></Can>
              </>
            ) : null}
          </div>
        }
      />
      {e.status === 'REJECTED' && e.rejectReason ? <div className="rounded-xl border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger-fg"><strong>{t('expenses.rejectReason')}:</strong> {e.rejectReason}</div> : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <section className="card">
            <div className="mb-3 text-3xl font-bold tabular-nums">{fmtEgp(e.amountEgp, lang)}</div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
              <Row label={t('expenses.category')}>{catName(e.category, lang)}</Row>
              <Row label={t('expenses.vehicle')}>{e.vehicle ? <Link className="hover:underline" to={`/vehicles/${e.vehicle.id}`}>{e.vehicle.plateAr}</Link> : '—'}</Row>
              <Row label={t('expenses.driver')}>{e.driver ? <Link className="hover:underline" to={`/drivers/${e.driver.id}`}>{e.driver.fullName}</Link> : '—'}</Row>
              <Row label={t('expenses.trip')}>{e.trip ? <Link className="hover:underline" dir="ltr" to={`/trips/${e.trip.id}`}>{e.trip.number}</Link> : '—'}</Row>
              <Row label={t('expenses.location')}>{e.placeName ?? e.locationText ?? (e.lat != null ? <span dir="ltr">{e.lat.toFixed(5)}, {e.lon?.toFixed(5)}</span> : '—')}</Row>
              <Row label={t('expenses.rebillable')}>{e.isRebillable ? t('common.yes') : t('common.no')}</Row>
              <Row label={t('expenses.submittedBy')}>{e.submittedByName ?? '—'}</Row>
              <Row label={t('expenses.reviewedBy')}>{e.reviewedByName ? `${e.reviewedByName} · ${fmtDateTime(e.reviewedAt, lang)}` : '—'}</Row>
              <Row label={t('expenses.createdAt')}>{fmtDateTime(e.createdAt, lang)}</Row>
            </dl>
            {e.notes ? <p className="mt-3 whitespace-pre-wrap border-t border-border-soft pt-3 text-sm">{e.notes}</p> : null}
          </section>

          {e.fuelLog ? (
            <section className="card">
              <h2 className="mb-3 text-sm font-bold text-text-2">{t('expenses.fuelDetails')}</h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Kpi label={t('expenses.liters')} value={`${fmtNumber(e.fuelLog.liters, lang, 2)} ${t('units.liter')}`} hint={t(`vehicles.fuelTypes.${e.fuelLog.fuelType}`)} />
                <Kpi label={t('expenses.pricePerLiter')} value={fmtNumber(e.fuelLog.pricePerLiter, lang, 3)} />
                <Kpi label={t('expenses.odometerKm')} value={e.fuelLog.odometerKm != null ? fmtNumber(e.fuelLog.odometerKm, lang) : '—'} hint={e.fuelLog.isFullTank ? t('expenses.fullTank') : t('expenses.partialFill')} />
                <Kpi label={t('expenses.fuel.kmSincePrev')} value={e.fuelLog.kmSincePrev != null ? `${fmtNumber(e.fuelLog.kmSincePrev, lang)} ${t('units.km')}` : '—'} />
                <Kpi label="L/100km" value={e.fuelLog.lPer100Km != null ? fmtNumber(e.fuelLog.lPer100Km, lang, 1) : '—'} hint={e.fuelLog.lPer100Km == null && e.status === 'APPROVED' ? t('expenses.fuel.needsPrevFull') : undefined} />
                <Kpi label={t('expenses.fuel.egpPerKm')} value={e.fuelLog.egpPerKm != null ? fmtNumber(e.fuelLog.egpPerKm, lang, 2) : '—'} />
              </div>
            </section>
          ) : null}
        </div>

        <section className="card">
          <h2 className="mb-3 text-sm font-bold text-text-2">{t('expenses.receipt')}</h2>
          {e.receiptUrl ? (
            <a href={e.receiptUrl} target="_blank" rel="noreferrer" className="block">
              {isImage ? <img src={e.receiptUrl} alt={t('expenses.receipt')} className="max-h-[480px] w-full rounded-xl border border-border-soft object-contain" /> : <span className="btn-secondary"><ExternalLink size={16} /> {t('expenses.openReceipt')}</span>}
            </a>
          ) : e.receiptFileId ? (
            <p className="text-sm text-text-muted">{t('expenses.receiptUnavailable')}</p>
          ) : (
            <p className="text-sm text-text-faint">{t('expenses.noReceipt')}</p>
          )}
        </section>
      </div>

      <ExpenseFormDialog open={editing} onClose={() => setEditing(false)} editing={e} />
      <Dialog
        open={rejecting}
        onClose={() => setRejecting(false)}
        title={t('expenses.reject')}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setRejecting(false)}>{t('common.cancel')}</button>
            <button className="btn-primary !bg-danger" disabled={reason.trim().length < 2 || review.isPending} onClick={() => review.mutate('reject')}>{review.isPending ? <Spinner /> : <X size={16} />} {t('expenses.reject')}</button>
          </>
        }
      >
        <Field label={t('expenses.rejectReason')} required>{(fid) => <textarea id={fid} className="input min-h-24" value={reason} onChange={(ev) => setReason(ev.target.value)} autoFocus />}</Field>
      </Dialog>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-text-muted">{label}</dt>
      <dd className="font-semibold">{children}</dd>
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl bg-surface-2 p-3">
      <div className="text-xs text-text-muted">{label}</div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
      {hint ? <div className="text-xs text-text-faint">{hint}</div> : null}
    </div>
  );
}
